import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import LinearProgress from "@mui/material/LinearProgress";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import {
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarExportContainer,
  GridToolbarFilterButton,
} from "@mui/x-data-grid-pro";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFleetCheckColumns } from "./hooks/useFleetCheckColumns";
import { useFleetStatusFeed } from "./hooks/useFleetStatusFeed";
import { buildFleetCheckRow, FleetCheckRow } from "./models/fleetCheckRow";
import { buildFleetCheckExportRows, fleetCheckFileName } from "./utilities/fleetCheckExport";
import { I18nGrid } from "@/components/data/I18nGrid";
import { useGeotabClient } from "@/components/GeotabClientContext";
import MetadataExcelExportMenuItem from "@/components/grid/CustomToolbar/MetadataExcelExportMenuItem";
import { useDecodeVins } from "@/hooks/devices/useDecodeVins";
import {
  DEVICE_FIELDS_WITH_FIRMWARE,
  useDevicesBySearchWithNumDevices,
} from "@/hooks/devices/useDevicesBySearch";
import { useStatusInfosByIds } from "@/hooks/statusInfos/useStatusInfosByIds";
import { useDebounced } from "@/hooks/useDebounced";
import { DeviceWithVehicleInfo } from "@/models/DeviceWithVehicleInfo";
import { LiveDataChecklist } from "@/pages/live-data/components/LiveDataChecklist";
import { createWorkbook, downloadBlob } from "@/utilities/xlsx";

const WINDOW_OPTIONS_IN_HOURS = [24, 72, 168] as const;

/** How many vehicles a single search may return. A fleet-wide check has to cover the fleet. */
const DEVICE_LIMIT = 50_000;

const LIST_REFETCH_MS = 10 * 60 * 1000;
const STATUS_REFETCH_MS = 2 * 60 * 1000;

/**
 * The installation checklist, asked of the whole fleet at once.
 *
 * The scan is started by hand rather than on mount: it walks every vehicle's readings over the
 * chosen window, which is far too expensive to spend because someone opened the page. Each row
 * expands into the same checklist the single-vehicle page shows.
 */
export default function VehicleDataCheckPage() {
  const { t, i18n } = useTranslation();
  const { database, username, me } = useGeotabClient();
  const [search, setSearch] = useState("");
  // Debounced, because every change asks Geotab for the device list again.
  const searchDebounced = useDebounced(search, 300);
  const [onlyIssues, setOnlyIssues] = useState(false);
  const [hoursBack, setHoursBack] = useState<number>(24);

  const { data, isLoading } = useDevicesBySearchWithNumDevices(
    searchDebounced,
    false,
    DEVICE_LIMIT,
    DEVICE_FIELDS_WITH_FIRMWARE,
    // The fleet's list does not change minute to minute, and refetching it pulls every vehicle
    // through the same request queue the scan is using.
    LIST_REFETCH_MS,
  );
  const devices = useMemo(() => (data?.devices ?? []) as DeviceWithVehicleInfo[], [data]);

  const deviceIds = useMemo(() => devices.map(device => device.id).sort(), [devices]);
  const { rowsByDeviceId, scan, start, stop } = useFleetStatusFeed(hoursBack, deviceIds);

  // Whether each unit is talking to Geotab right now. Independent of the scan: it says whether a
  // vehicle that reported nothing is silent or merely was not driven.
  // Asked rarely: the page wants to know whether a unit talks at all, not what it is doing right
  // now, and this is one request per two thousand vehicles.
  const { data: statusInfo } = useStatusInfosByIds(deviceIds, [], STATUS_REFETCH_MS);
  const statusInfoMap = useMemo(() => statusInfo ?? {}, [statusInfo]);

  // Fixed properties of the vehicle, decoded once and unaffected by the scan window.
  const vins = useMemo(
    () =>
      devices
        .map(device => device.vehicleIdentificationNumber)
        .filter((vin): vin is string => !!vin),
    [devices],
  );
  const { data: vinDetails = [] } = useDecodeVins(vins);
  const vinDecodedByVin = useMemo(
    () =>
      new Map(
        vinDetails.map(detail => [
          detail.vin,
          [detail.make, detail.model].filter(Boolean).join(" "),
        ]),
      ),
    [vinDetails],
  );

  const rows = useMemo<FleetCheckRow[]>(
    () =>
      devices
        .map(device =>
          buildFleetCheckRow({
            device,
            rows: scan.scannedIds.has(device.id) ? (rowsByDeviceId[device.id] ?? []) : null,
            isCommunicating: statusInfoMap[device.id]?.isDeviceCommunicating,
            vinDecoded: device.vehicleIdentificationNumber
              ? vinDecodedByVin.get(device.vehicleIdentificationNumber)
              : undefined,
          }),
        )
        // "Not scanned" is not an issue -- nobody has looked yet.
        .filter(row => !onlyIssues || (row.status !== "ok" && row.status !== "notScanned")),
    [devices, rowsByDeviceId, statusInfoMap, vinDecodedByVin, scan.scannedIds, onlyIssues],
  );

  const hasScanned = scan.window !== null;

  const columns = useFleetCheckColumns();

  const handleExportExcel = useCallback(() => {
    if (!scan.window) {
      return;
    }

    const exportedAt = new Date();
    const workbook = createWorkbook(
      buildFleetCheckExportRows(
        rows,
        {
          database: database ?? "",
          user: (me as { name?: string } | null)?.name?.trim() || (username ?? ""),
          // The window the scan actually covered, not one derived from the current setting: the
          // setting can be changed after a scan, and then the sheet would name a period the
          // numbers in it never came from.
          from: scan.window.from,
          to: scan.window.to,
          exportedAt,
          language: i18n.language,
        },
        t,
      ),
    );
    downloadBlob(workbook, fleetCheckFileName(database ?? "", exportedAt));
  }, [database, i18n.language, me, rows, scan.window, t, username]);

  const detailPanelContent = useCallback(
    ({ row }: { row: FleetCheckRow }) => (
      <Box sx={{ p: 1.5 }}>
        <LiveDataChecklist device={row.device} rows={row.rows} />
      </Box>
    ),
    [],
  );
  const detailPanelHeight = useCallback(() => "auto" as const, []);

  // Given as a component rather than through `slotProps`, which would need the props declared in
  // a global module augmentation to be type-checked at all. Nothing to export before a scan: the
  // sheet would claim the whole fleet had failed.
  const toolbar = useCallback(
    () => (
      <FleetCheckToolbar
        onExportExcel={handleExportExcel}
        exportDisabled={!hasScanned || rows.length === 0}
      />
    ),
    [handleExportExcel, hasScanned, rows.length],
  );

  return (
    <Stack spacing={2} sx={{ p: { xs: 1, md: 2 }, height: "100%" }}>
      <Stack direction={{ xs: "column", md: "row" }} spacing={2} justifyContent="space-between">
        <Typography color="text.secondary" variant="body2">
          {t(
            "vehicleDataCheck.subtitle",
            "The installation checklist, asked of every vehicle at once",
          )}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 160 }} disabled={scan.isRunning}>
            <InputLabel>{t("vehicleDataCheck.timeWindow", "Window")}</InputLabel>
            <Select
              value={hoursBack}
              label={t("vehicleDataCheck.timeWindow", "Window")}
              onChange={event => setHoursBack(Number(event.target.value))}
            >
              {WINDOW_OPTIONS_IN_HOURS.map(hours => (
                <MenuItem key={hours} value={hours}>
                  {hours === 24
                    ? t("vehicleDataCheck.windowHours", "{{count}} hours", { count: hours })
                    : t("vehicleDataCheck.windowDays", "{{count}} days", { count: hours / 24 })}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {scan.isRunning ? (
            <Button color="error" startIcon={<StopIcon />} onClick={stop}>
              {t("vehicleDataCheck.stopScan", "Stop")}
            </Button>
          ) : (
            <Button
              variant="contained"
              startIcon={<PlayArrowIcon />}
              onClick={start}
              disabled={deviceIds.length === 0}
            >
              {t("vehicleDataCheck.startScan", "Start scan")}
            </Button>
          )}
        </Stack>
      </Stack>

      {scan.error && (
        <Alert severity="error">
          {t("vehicleDataCheck.scanFailed", "The scan failed")}: {scan.error.message}
        </Alert>
      )}

      {(scan.isRunning || scan.pagesLoaded > 0) && (
        <Box>
          {scan.isRunning && <LinearProgress />}
          <Typography variant="caption" color="text.secondary">
            {t(
              "vehicleDataCheck.scanProgress",
              "{{records}} readings read · {{devices}} vehicles have reported",
              { records: scan.recordsProcessed, devices: scan.deviceCount },
            )}
          </Typography>
        </Box>
      )}

      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
        <TextField
          size="small"
          label={t("vehicleDataCheck.search", "Vehicle or serial number")}
          value={search}
          onChange={event => setSearch(event.target.value)}
          sx={{ minWidth: { sm: 300 } }}
        />
        <Button
          variant={onlyIssues ? "contained" : "outlined"}
          onClick={() => setOnlyIssues(current => !current)}
        >
          {t("vehicleDataCheck.onlyIssues", "Only issues")}
        </Button>
        <Typography color="text.secondary" variant="body2" sx={{ ml: { sm: "auto" } }}>
          {t("vehicleDataCheck.vehicleCount", "{{count}} vehicles", { count: rows.length })}
        </Typography>
      </Stack>

      <Paper sx={{ flex: 1, minHeight: 420 }}>
        <I18nGrid
          rows={rows}
          columns={columns}
          loading={isLoading}
          disableRowSelectionOnClick
          density="compact"
          pagination
          pageSizeOptions={[25, 50, 100]}
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } },
            pinnedColumns: { left: ["name"], right: ["status"] },
          }}
          getDetailPanelHeight={detailPanelHeight}
          getDetailPanelContent={detailPanelContent}
          slots={{ toolbar }}
          sx={{ border: 0 }}
        />
      </Paper>
    </Stack>
  );
}

interface ToolbarProps {
  onExportExcel: () => void;
  exportDisabled: boolean;
}

/**
 * The grid's toolbar.
 *
 * The page's own export is the only one offered. The grid's built-in CSV export would write the
 * grid's columns, which are not the sheet's columns -- two exports of different shapes under one
 * button, and neither would carry what was queried.
 */
function FleetCheckToolbar({ onExportExcel, exportDisabled }: ToolbarProps) {
  return (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarDensitySelector />
      <GridToolbarExportContainer>
        <MetadataExcelExportMenuItem onExport={onExportExcel} disabled={exportDisabled} />
      </GridToolbarExportContainer>
    </GridToolbarContainer>
  );
}
